import React from 'react';
import { Form, Alert, Tooltip, Typography, Select } from '../../components';
import { WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { makeNiceName } from '../../lib/helpers';

const { Text } = Typography;

/**
 * Reusable Target Cloud Selector component
 * @param {Array} processedCloudAccounts - Processed cloud accounts with disabled flags
 * @param {Function} onChange - Handler for target account selection
 * @param {boolean} shouldPreselect - Whether account should be preselected
 * @param {boolean} isMigrateByVM - Whether migration is by VM
 * @param {Object} selectedAccount - Currently selected account
 * @param {boolean} useFormItem - Whether to wrap in Form.Item (default: true)
 * @param {boolean} showTooltip - Whether to show info tooltip (default: true)
 * @param {boolean} showApplianceWarning - Whether to show appliance upgrade warning (default: true)
 * @param {string} placeholder - Select placeholder text
 * @param {string} label - Label text
 * @param {string} value - Current selected value
 * @param {boolean} loading - Loading state
 * @param {boolean} disabled - Disabled state
 * @param {boolean} allowClear - Allow clearing selection
 * @param {Object} style - Custom styles
 * @param {string} className - Custom CSS class
 */
export const TargetCloudSelector = ({
    processedCloudAccounts = [],
    onChange,
    shouldPreselect = false,
    isMigrateByVM = false,
    selectedAccount = null,
    useFormItem = true,
    showTooltip = true,
    showApplianceWarning = true,
    placeholder = 'Select Target Cloud',
    label = 'Select Target Cloud',
    value,
    loading = false,
    disabled = false,
    allowClear = false,
    style = {},
    className = '',
    ...otherProps
}) => {
    const targetOptions = processedCloudAccounts
        .map((account) => ({
            value: account.id,
            disabled: account.disabled,
            label: `${account.name}${` (${makeNiceName(account.cloud_type, true)})`}`,
        }))
        .sort((a, b) => a.disabled - b.disabled || a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    const labelWithTooltip = showTooltip ? (
        <span>
            {label}{' '}
            <Tooltip title="The Migration Appliance associated with the selected Target Cloud environment will be used to retrieve information about the source(s) such as OS type, CPU, memory, etc.">
                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
            </Tooltip>
        </span>
    ) : (
        label
    );

    const selectComponent = (
        <Select
            placeholder={placeholder}
            onChange={onChange}
            disabled={disabled}
            options={targetOptions}
            value={value}
            loading={loading}
            allowClear={allowClear}
            style={style}
            className={className}
            {...otherProps}
        />
    );

    const applianceWarning = showApplianceWarning && selectedAccount?.appliance?.needs_upgrade && (
        <Alert
            title={
                <span>
                    <WarningOutlined /> This Migration Appliance version is out-of-date. To proceed, please{' '}
                    <a
                        href="http://docs.rivermeadow.com/upgrade-cloud-appliance"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        upgrade
                    </a>{' '}
                    your Migration Appliance.
                </span>
            }
            type="warning"
            showIcon={false}
            style={{ marginBottom: 16 }}
        />
    );

    if (useFormItem) {
        return (
            <>
                <Form.Item
                    label={labelWithTooltip}
                    name="targetAccount"
                    rules={[{ required: true, message: 'Please select a Target Cloud' }]}
                >
                    {selectComponent}
                </Form.Item>
                {applianceWarning}
            </>
        );
    }

    return (
        <>
            {label && <Text style={{ display: 'block', marginBottom: 8 }}>{labelWithTooltip}</Text>}
            {selectComponent}
            {applianceWarning}
        </>
    );
};

export default TargetCloudSelector;
